import React, {
    useEffect,
    useContext,
    useCallback,
} from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Cart } from '@pegasus/cart';
import { AddItemProps } from '@pegasus/cart/types/services';
import { ConfigContext } from '@client/hoc/config/ConfigContext';
import { ADD_CART_SIZE_SELECTION_WARNING } from '@client/config/constants';
import { track, getTrackingDataRemoveFromCart } from '@client/helpers/tracking';
import { setCart, resetCart } from './redux/actions';
import { getCart } from './redux/selectors';
import * as cartServices from './services';
import { transformCartItems } from './helpers';

interface AddItemPropsWithTracking extends AddItemProps {
    itemSize: string;
}
interface ReplaceItemProps extends AddItemPropsWithTracking {
    replaceSimpleSku: string;
}

interface CartActions {
    onItemRemove: (sku: string) => Promise<void>;
    onExtendCart: () => Promise<void>;
    onExpiredCart: () => void;
};

// we use a global variable to trigger
// the fetch of the cart on mount only once
let shouldFetch = true;

/**
 * Cart functions hook.
 * This hook is good as long as we don't need
 * to share the state among different components
 * when needed, extract the state logic into context API or redux
 */
export const useCart = (): [Cart, CartActions] => {
    const { config } = useContext(ConfigContext);
    const cart = useSelector(getCart);
    const dispatch = useDispatch();
    const store = useStore();

    const trackRemoveFromCart = useCallback((simpleSkuRemoved: string) => {
        try {
            const cartItem = cart.items.find(({ simpleSku }) => simpleSku === simpleSkuRemoved);
            const trackingDataRemoveFromCart = getTrackingDataRemoveFromCart({
                cartItem,
            });
            track('removeFromCart', trackingDataRemoveFromCart);
        }
        catch(e) {
            console.error(e);
        }
    }, [cart]);

    const fetchCart = useCallback(() => cartServices.getCart().then(currCart => {
        if (currCart) {
            dispatch(setCart({
                ...currCart,
                items: transformCartItems(currCart.items, config),
            }));
        }
    }).catch(console.error), []);

    const onItemRemove = useCallback(
        (itemSimpleSku: string) => cartServices.deleteCartItem(itemSimpleSku)
            .then(() => {
                trackRemoveFromCart(itemSimpleSku);

                return fetchCart();
            })
            .catch(console.error),
    [cart]);

    const onExtendCart = useCallback(
        () => cartServices.extendCart()
            .then(fetchCart)
            .catch(console.error)
    , []);

    const onExpiredCart = useCallback(
        () => {
            dispatch(resetCart())
        }
    , []);

    useEffect(() => {
        if (shouldFetch) {
            shouldFetch = false;
            fetchCart();
        }
    }, []);

    return [
        cart,
        {
            onItemRemove,
            onExtendCart,
            onExpiredCart,
        }
    ];
};
